(function () {
  'use strict';

  const configNode = document.getElementById('aboffs-region-config');
  if (!configNode || window.AboffsRegion) return;
  const config = JSON.parse(configNode.textContent);
  config.regions =
    config.regions && typeof config.regions === 'object' && !Array.isArray(config.regions)
      ? Object.fromEntries(
          Object.entries(config.regions).filter(
            ([code, name]) => /^[A-Z]{2}$/.test(code) && typeof name === 'string' && name.trim(),
          ),
        )
      : {};
  const hasRegions = Object.keys(config.regions).length > 0;
  const storageKey = 'aboffs_selected_region';
  const regionAttribute = 'Aboffs-Pricing-Region';
  const regionSuggestionUrl =
    'https://geolocation.developers-efd.workers.dev/?x-api-key=5de3921b-dd40-4eb0-8578-236de4f2f3a4';
  const root = window.Shopify?.routes?.root || '/';
  const dialog = document.getElementById('aboffs-region-dialog');
  let revision = 0;
  let pending = Promise.resolve();
  let busy = false;
  let desiredState;
  let pickupRegion = '';
  let suggestedRegion = '';

  function normalize(value) {
    const text = String(value || '')
      .trim()
      .toUpperCase();
    return (
      Object.keys(config.regions).find(
        (code) => code === text || config.regions[code].toUpperCase() === text,
      ) || ''
    );
  }

  function readStorage(storage, key) {
    try {
      return window[storage].getItem(key) || '';
    } catch {
      return '';
    }
  }

  function writeStorage(storage, key, value) {
    try {
      window[storage].setItem(key, value);
    } catch {
      /* Cart attributes remain the persistent source. */
    }
  }

  // Shoppers outside the US get the default store and, unless the app setting is off, the same prompt.
  const outsideUs = (() => {
    const country = String(window.Shopify?.country || '').toUpperCase();
    const active = String(window.Shopify?.currency?.active || '').toUpperCase();
    const shop = String(config.currency || '').toUpperCase();
    return Boolean((country && country !== 'US') || (active && shop && active !== shop));
  })();

  function pickupState(attributes) {
    return attributes?.['Checkout-Method'] === 'pickup' && attributes['Pickup-Location-Id']
      ? normalize(attributes['Pickup-Location-Region'])
      : '';
  }

  // The default catalog's store: its prices are the Shopify prices, so it's what shoppers see until they choose.
  const defaultRegion = normalize(config.defaultRegion) || Object.keys(config.regions)[0] || '';
  const queryRegion = normalize(new URLSearchParams(window.location.search).get('region_id'));
  let pickupRequiresInteraction = Boolean(queryRegion);
  const cartRegion =
    (config.attributes?.['Checkout-Method'] === 'pickup' && config.attributes?.['Pickup-Location-Id']
      ? config.attributes['Pickup-Location-Region']
      : '') || config.attributes?.[regionAttribute];
  const savedRegion = cartRegion || readStorage('localStorage', storageKey);
  // The region Liquid rendered prices and the logo for (snippets/aboffs-store-region.liquid).
  const renderedState = normalize(cartRegion) || defaultRegion;
  let requiresRegionSelection = !queryRegion && Boolean(savedRegion) && !normalize(savedRegion);
  const isFirstVisit = !queryRegion && !savedRegion;
  let state = queryRegion || normalize(savedRegion) || defaultRegion;

  if (requiresRegionSelection) writeStorage('localStorage', storageKey, '');

  desiredState = state;

  function closeRegionDisclosure(disclosure, restoreFocus = false) {
    if (!disclosure) return;
    const trigger = disclosure.querySelector('[data-aboffs-region-trigger]');
    const panel = disclosure.querySelector('[data-aboffs-region-panel]');
    trigger?.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
    if (restoreFocus) trigger?.focus();
  }

  function renderConfirm() {
    const select = dialog?.querySelector('[data-aboffs-region-select]');
    const confirm = dialog?.querySelector('[data-aboffs-region-confirm]');
    const region = normalize(select?.value) || state;
    if (confirm) confirm.textContent = config.messages.confirm.replace('{region}', config.regions[region] || '');
    const hint = dialog?.querySelector('[data-aboffs-region-suggestion]');
    if (hint) hint.hidden = !suggestedRegion || region !== suggestedRegion;
  }

  function render() {
    document.querySelectorAll('[data-aboffs-region-select]').forEach((select) => {
      if (!dialog?.open || !dialog.contains(select)) select.value = state;
      select.disabled = busy || !hasRegions;
    });
    document.querySelectorAll('[data-aboffs-region-disclosure]').forEach((disclosure) => {
      const trigger = disclosure.querySelector('[data-aboffs-region-trigger]');
      const value = disclosure.querySelector('[data-aboffs-region-value]');
      if (trigger) trigger.disabled = busy || !hasRegions;
      if (value) value.textContent = state ? config.regions[state] : config.messages.unavailable;
      disclosure.querySelectorAll('[data-aboffs-region-option]').forEach((option) => {
        const selected = normalize(option.dataset.aboffsRegionOption) === state;
        option.disabled = busy;
        option.setAttribute('aria-checked', String(selected));
      });
    });
    dialog?.querySelector('button[type="submit"]')?.toggleAttribute('disabled', busy || !hasRegions);
    renderConfirm();
  }

  function notice(message) {
    const node = document.querySelector('[data-aboffs-region-status]');
    if (!node) return;
    node.textContent = message;
    node.hidden = !message;
  }

  async function selectRegion(value, options = {}) {
    const next = normalize(value);
    if (!next || busy) {
      render();
      return;
    }
    busy = true;
    render();
    notice('');
    const previousError = dialog?.querySelector('[data-aboffs-region-error]');
    if (previousError) previousError.hidden = true;
    try {
      const attributes = { [regionAttribute]: next };
      let clearedPickup = false;
      if (options.source !== 'pickup') {
        const cartResponse = await fetch(root + 'cart.js', {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!cartResponse.ok) throw new Error('Cart unavailable');
        const cart = await cartResponse.json();
        if (cart.attributes?.['Pickup-Location-Id'] && pickupState(cart.attributes) !== next) {
          Object.keys(cart.attributes)
            .filter((key) => key.startsWith('Pickup-'))
            .forEach((key) => {
              attributes[key] = '';
            });
          clearedPickup = true;
        }
      }
      const response = await fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      if (!response.ok) throw new Error('Region update failed');
      const updatedCart = await response.json();
      state = next;
      requiresRegionSelection = false;
      writeStorage('localStorage', storageKey, state);
      render();
      const url = new URL(window.location.href);
      if (options.source !== 'query') url.searchParams.delete('region_id');
      let refreshPage =
        options.reload === true || (config.serverPricing && !window.AboffsPriceSnapshots && renderedState !== next);
      if (clearedPickup) {
        pickupRequiresInteraction = true;
        const widget = document.getElementById('storePickupApp');
        if (widget && window.Zapiet?.start && window.ZapietWidgetConfig) {
          try {
            window.Zapiet.start(window.ZapietWidgetConfig);
          } catch {
            refreshPage = refreshPage || options.source !== 'query';
          }
        } else if (widget && options.source !== 'query') {
          refreshPage = true;
        }
      } else if (
        !refreshPage &&
        options.source !== 'pickup' &&
        pickupRegion &&
        pickupRegion !== next &&
        window.Zapiet?.getSelectedCheckoutMethod?.() === 'pickup' &&
        window.ZapietWidgetConfig
      ) {
        // Reloading the widget lets Zapiet preselect a location in the new region
        // (snippets/storepickup-addons.liquid lists that region's locations first).
        pickupRequiresInteraction = true;
        try {
          window.Zapiet.start(window.ZapietWidgetConfig);
        } catch {
          /* The shopper can still change the pickup location in the widget. */
        }
      }
      if (refreshPage) {
        window.location.replace(url.toString());
      } else {
        window.history.replaceState(window.history.state, '', url.toString());
        document.dispatchEvent(
          new CustomEvent('aboffs:regionChanged', { detail: { state, revision, cart: updatedCart } }),
        );
        if (dialog?.open) dialog.close();
        if (options.source === 'pickup')
          notice(config.messages.pickup.replace('{region}', config.regions[state]));
      }
    } catch (error) {
      if (desiredState === next) {
        desiredState = state;
        document.dispatchEvent(new CustomEvent('aboffs:regionChanged', { detail: { state, revision } }));
      }
      render();
      notice(config.messages.error);
      const errorNode = dialog?.querySelector('[data-aboffs-region-error]');
      if (errorNode) {
        errorNode.textContent = config.messages.error;
        errorNode.hidden = false;
      }
      throw error;
    } finally {
      busy = false;
      render();
    }
  }

  function enqueueSelection(value, options) {
    const next = normalize(value);
    if (!next) {
      render();
      return Promise.resolve();
    }
    desiredState = next;
    revision += 1;
    pending = pending.catch(() => {}).then(() => selectRegion(value, options));
    return pending;
  }

  function acceptPickup(params) {
    if (params?.method !== 'pickup') return;
    const next = normalize(params.location?.region_code) || normalize(params.location?.region);
    pickupRegion = next;
    if (pickupRequiresInteraction || requiresRegionSelection || !next || next === desiredState) return;
    enqueueSelection(next, { source: 'pickup', reload: false }).catch(() => {});
  }

  function bindZapiet() {
    if (!window.ZapietEvent?.listen) return false;
    window.ZapietEvent.listen('locationSelected', acceptPickup);
    window.ZapietEvent.listen('checkoutEnabled', acceptPickup);
    return true;
  }

  /** The closest store, when geolocation finds one; the dialog marks it "Closest to you". */
  async function closestRegion() {
    try {
      const response = await fetch(regionSuggestionUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'omit',
        signal: AbortSignal.timeout(4000),
        cache: 'no-store',
      });
      if (!response.ok) return '';
      const data = await response.json();
      const directSuggestion = data?.state || data?.regionCode;
      const countryCode = String(data?.data?.country?.iso_code || '').toUpperCase();
      const workerSuggestion =
        countryCode === 'US' ? data?.data?.region?.iso_code || data?.data?.region?.names?.en : '';
      return normalize(directSuggestion || workerSuggestion);
    } catch {
      return ''; /* The dialog works without geolocation. */
    }
  }

  /** First visits always end with a store: the one chosen, or the default when the dialog is closed. */
  async function promptForRegion() {
    if (!hasRegions || !isFirstVisit || !dialog?.showModal || dialog.open) return;
    if (outsideUs && !config.promptOutsideUs) return;
    const select = dialog.querySelector('[data-aboffs-region-select]');
    select.value = state;
    let touched = false;
    select.addEventListener('change', () => (touched = true), { once: true });
    dialog.showModal();
    render();
    const closest = await closestRegion();
    if (!closest || !dialog.open) return;
    suggestedRegion = closest;
    if (!touched) select.value = closest;
    renderConfirm();
  }

  window.AboffsRegion = Object.freeze({
    getState: () => state,
    getRevision: () => revision,
    whenSettled: async () => {
      let request;
      do {
        request = pending;
        await request;
      } while (request !== pending);
    },
    stateForCheckout: (attributes) => {
      if (!hasRegions) throw new Error(config.messages.unavailable);
      if (requiresRegionSelection) throw new Error(config.messages.required);
      if (attributes?.['Checkout-Method'] === 'pickup') {
        const pickup = pickupState(attributes);
        if (!pickup) throw new Error(config.messages.required);
        if (pickupRequiresInteraction && pickup !== state) throw new Error(config.messages.confirmPickup);
        return pickup;
      }
      if (!normalize(state)) throw new Error(config.messages.required);
      return state;
    },
  });
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-aboffs-region-trigger]');
    if (trigger) {
      const disclosure = trigger.closest('[data-aboffs-region-disclosure]');
      const panel = disclosure?.querySelector('[data-aboffs-region-panel]');
      const opening = trigger.getAttribute('aria-expanded') !== 'true';
      document.querySelectorAll('[data-aboffs-region-disclosure]').forEach((item) => {
        if (item !== disclosure) closeRegionDisclosure(item);
      });
      trigger.setAttribute('aria-expanded', String(opening));
      if (panel) panel.hidden = !opening;
      return;
    }
    const option = event.target.closest?.('[data-aboffs-region-option]');
    if (option) {
      const disclosure = option.closest('[data-aboffs-region-disclosure]');
      closeRegionDisclosure(disclosure);
      enqueueSelection(option.dataset.aboffsRegionOption).catch(() => {});
      return;
    }
    document
      .querySelectorAll('[data-aboffs-region-disclosure]')
      .forEach((item) => closeRegionDisclosure(item));
  });
  document.addEventListener('keydown', (event) => {
    const disclosure = event.target.closest?.('[data-aboffs-region-disclosure]');
    if (!disclosure) return;
    const trigger = disclosure.querySelector('[data-aboffs-region-trigger]');
    const panel = disclosure.querySelector('[data-aboffs-region-panel]');
    const options = [...disclosure.querySelectorAll('[data-aboffs-region-option]:not(:disabled)')];
    if (event.key === 'Escape') {
      event.preventDefault();
      closeRegionDisclosure(disclosure, true);
      return;
    }
    if (event.target === trigger && event.key === 'ArrowDown') {
      event.preventDefault();
      trigger.setAttribute('aria-expanded', 'true');
      if (panel) panel.hidden = false;
      options[0]?.focus();
      return;
    }
    if (!options.includes(event.target) || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    const offset = event.key === 'ArrowDown' ? 1 : -1;
    options[(options.indexOf(event.target) + offset + options.length) % options.length]?.focus();
  });
  document.addEventListener('focusout', (event) => {
    const disclosure = event.target.closest?.('[data-aboffs-region-disclosure]');
    if (disclosure && !disclosure.contains(event.relatedTarget)) closeRegionDisclosure(disclosure);
  });
  document.addEventListener('change', (event) => {
    if (!event.target.matches?.('[data-aboffs-region-select]')) return;
    if (dialog?.contains(event.target)) {
      renderConfirm();
      return;
    }
    enqueueSelection(event.target.value).catch(() => {});
  });
  dialog?.querySelector('form').addEventListener('submit', (event) => {
    event.preventDefault();
    enqueueSelection(dialog.querySelector('select').value).catch(() => {});
  });
  // There's no way to leave without a store: closing the dialog keeps the default one.
  dialog?.addEventListener('cancel', (event) => {
    event.preventDefault();
    if (!busy) enqueueSelection(defaultRegion).catch(() => {});
  });
  dialog?.addEventListener('close', render);
  document.addEventListener('shopify:section:load', render);
  // Restored widget events must not undo an explicit URL/header region selection.
  const acceptPickupInteraction = (event) => {
    if (event.isTrusted && event.target.closest?.('#storePickupApp')) {
      pickupRequiresInteraction = false;
      requiresRegionSelection = false;
    }
  };
  document.addEventListener('pointerdown', acceptPickupInteraction, true);
  document.addEventListener('keydown', acceptPickupInteraction, true);
  document.addEventListener('change', acceptPickupInteraction, true);
  render();
  if (!hasRegions) {
    notice(config.messages.unavailable);
  } else if (requiresRegionSelection) {
    notice(config.messages.removed);
    const errorNode = dialog?.querySelector('[data-aboffs-region-error]');
    if (errorNode) {
      errorNode.textContent = config.messages.removed;
      errorNode.hidden = false;
    }
    if (dialog?.showModal && !dialog.open) dialog.showModal();
  }
  if (!bindZapiet()) {
    const timer = setInterval(() => {
      if (bindZapiet()) clearInterval(timer);
    }, 250);
    setTimeout(() => clearInterval(timer), 60000);
  }
  if (queryRegion) {
    enqueueSelection(queryRegion, { source: 'query', reload: false }).catch(() => {});
  } else {
    promptForRegion();
  }
})();
